import { useStableObject } from '@atlassian/post-office-context';
import {
	Placement as InternalPlacement,
	type PlacementOnEvent,
} from '@atlassian/post-office-placement-shared';
import { createPlacementComponent } from '@post-office/placement-common/utils/create-placement-component';
import { PerformanceTrackingSegment } from '@post-office/placement-contexts/create-placement-component/providers';
import type { PlacementComponent as PlacementComponentType } from '@post-office/placement-contexts/create-placement-component/types';
// @ts-expect-error: 'React' is declared but its value is never read
// React import required to render in Jira SSR
import React, {
	type FunctionComponent,
	type PropsWithChildren,
	type ReactElement,
	memo,
	useMemo,
} from 'react';

import ErrorBoundary from './ErrorBoundary';
import { ANALYTICS_CHANNEL } from './constants';

const STABLE_OBJECT: LocalPlacementProps = {};

type LocalPlacementProps = Record<string, unknown>;

type PlacementID = string;

interface BaseProps {
	placementId: PlacementID;
	PlacementComponent: PlacementComponentType<LocalPlacementProps>;
	placementProps?: LocalPlacementProps;
	ErrorFallback?: FunctionComponent;
	Fallback?: ReactElement | null;
	disableInitialData?: boolean;
	onEvent?: PlacementOnEvent;
}
export type PlacementProps = PropsWithChildren<BaseProps>;

const Placement: FunctionComponent<PlacementProps> = ({
	placementId,
	placementProps,
	children,
	ErrorFallback,
	PlacementComponent,
	disableInitialData = false,
	onEvent,
}) => {
	const MemoizedPlacement = useMemo(
		() => createPlacementComponent(placementId, PlacementComponent),
		[placementId, PlacementComponent],
	);

	const stableLocalPlacementProps = useStableObject<LocalPlacementProps>(
		placementProps ?? STABLE_OBJECT,
	);

	return (
		<PerformanceTrackingSegment placementId={placementId}>
			<InternalPlacement
				placementId={placementId}
				consumptionType="local"
				onEvent={onEvent}
				disableInitialData={disableInitialData}
			>
				{({ initialData, context, onEvent: sharedOnEvent }) => (
					<ErrorBoundary
						analyticsChannel={ANALYTICS_CHANNEL}
						placementId={placementId}
						onError={sharedOnEvent}
						fallback={ErrorFallback}
					>
						<MemoizedPlacement
							initialData={initialData}
							context={context}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore - Confluence Local Consumption
							errorFallback={ErrorFallback}
							onEvent={sharedOnEvent}
							{...stableLocalPlacementProps}
						>
							{children}
						</MemoizedPlacement>
					</ErrorBoundary>
				)}
			</InternalPlacement>
		</PerformanceTrackingSegment>
	);
};

export default memo(Placement);
